import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  Empty,
  Tabs,
  Modal,
  message,
  Spin,
  Statistic,
  Row,
  Col,
  Card,
  Input,
  Space,
  Popconfirm
} from 'antd'
import {
  PlusOutlined,
  AppstoreOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
  SortAscendingOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import InventoryProductCard from '../components/inventory/InventoryProductCard'
import InventoryCategoryForm from '../components/inventory/InventoryCategoryForm'
import ProductLedgerModal from '../components/inventory/ProductLedgerModal'
import StockAdjustModal from '../components/inventory/StockAdjustModal'
import InventoryPurchasePage from './InventoryPurchasePage'
import {
  getInventoryProducts,
  getInventoryCategories,
  deleteInventoryProduct,
  getInventoryStatistics,
  deleteInventoryCategory
} from '../api/inventory'
import { reorderStoreProducts } from '../api/store'
import api from '../utils/api'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { usePermission } from '../hooks/usePermission'
import { useMembership } from '../hooks/useMembership'
import { useMediaQuery } from 'react-responsive'

const { Search } = Input

const PageContainer = styled.div`
  padding: 24px;
  background: #f0f2f5;
  min-height: 100vh;

  @media screen and (max-width: 768px) {
    padding: 16px;
    overflow-x: hidden;
  }
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const Title = styled.h1`
  margin: 0;
  color: #1a1a1a;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media screen and (max-width: 768px) {
    font-size: 24px;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media screen and (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;

    /* 直向堆疊、每顆滿版，避免長文字（含數量）撐爆寬度而超出畫面 */
    button {
      flex: 1 1 100%;
      min-width: 0;
      white-space: normal;
    }
  }
`

const StatsCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-statistic-title {
    font-size: 14px;
    color: #666;
  }

  .ant-statistic-content {
    font-size: 24px;
    font-weight: 600;
  }

  @media screen and (max-width: 768px) {
    .ant-statistic-content {
      font-size: 20px;
    }
  }
`

const StyledTabs = styled(Tabs)`
  margin-bottom: 24px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    font-weight: 500;
    padding: 12px 20px;
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: #d5728a;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 12px;

    .ant-tabs-nav {
      overflow-x: auto;
      overflow-y: hidden;
    }

    .ant-tabs-tab {
      padding: 8px 16px;
      font-size: 14px;
    }
  }
`

const SearchSection = styled.div`
  margin-bottom: 24px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media screen and (max-width: 768px) {
    padding: 12px;
  }
`

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const EmptyState = styled(Empty)`
  margin: 48px 0;
  padding: 40px;
  background: white;
  border-radius: 12px;

  @media screen and (max-width: 768px) {
    margin: 24px 0;
    padding: 24px;
  }
`

const DragHandle = styled.div`
  cursor: grab;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #666;
  border-radius: 6px;
  &:active {
    cursor: grabbing;
  }
  &:hover {
    background: #f0f0f0;
    color: #1890ff;
  }
`

const InventoryProductsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { businessId } = useAuth()
  const { getPlanLimit, plan: planKey } = useMembership()
  const { can } = usePermission()
  // 只有具備 inventory:manage（或 *）才能新增、編輯、刪除商品；inventory:view 僅能瀏覽
  const canManageInventory = can('inventory:manage') || can('inventory:*') || can('*')
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  // 分類與搜尋以網址為準：從編輯商品頁返回時，元件會重新掛載，
  // 只有寫在網址上的條件還原得回來。
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get('tab') || 'all'
  )
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState(
    () => searchParams.get('q') || ''
  )
  const [statistics, setStatistics] = useState(null)
  const [isSortMode, setIsSortMode] = useState(false)
  const [reordering, setReordering] = useState(false)
  // 進貨 Modal：null = 關閉；否則為預選商品 ID
  const [purchaseProductId, setPurchaseProductId] = useState(null)
  // 用 nonce 強制 Modal 重掛載，確保表單／預選狀態乾淨；
  // 例如使用者連續對不同商品快速進貨。
  const [purchaseModalNonce, setPurchaseModalNonce] = useState(0)
  // 異動明細 Modal：null = 關閉；否則為 { id, name }
  const [ledgerProduct, setLedgerProduct] = useState(null)
  // 調整庫存 Modal：null = 關閉；否則為商品物件
  const [adjustProduct, setAdjustProduct] = useState(null)

  const canViewFinance = can('finance:view')

  // 開啟單品進出庫異動明細
  const handleViewLedger = (productId, product) => {
    setLedgerProduct({
      id: productId,
      name: product?.name || product?.title || ''
    })
  }

  // 開啟調整庫存
  const handleAdjustStock = (productId, product) => {
    setAdjustProduct(product)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // 獲取數據
  const fetchData = async () => {
    setLoading(true)
    try {
      const [productsRes, categoriesRes, statsRes] = await Promise.all([
        getInventoryProducts({
          page: 1,
          pageSize: 1000
        }),
        getInventoryCategories(),
        getInventoryStatistics().catch(() => null)
      ])

      // 處理商品數據
      const productsData =
        productsRes.data?.data?.items || productsRes.data?.items || []
      setProducts(productsData)

      // 處理分類數據 - 支持多種響應格式
      let categoriesData = []
      if (Array.isArray(categoriesRes.data?.data)) {
        categoriesData = categoriesRes.data.data
      } else if (Array.isArray(categoriesRes.data)) {
        categoriesData = categoriesRes.data
      } else if (
        categoriesRes.data &&
        typeof categoriesRes.data === 'object' &&
        categoriesRes.data.code === 200
      ) {
        // 處理 { code: 200, message: "成功", data: [...] } 格式
        categoriesData = Array.isArray(categoriesRes.data.data)
          ? categoriesRes.data.data
          : []
      }
      console.log('分類數據:', categoriesData)
      setCategories(categoriesData)

      // 處理統計數據
      if (statsRes?.data?.data) {
        setStatistics(statsRes.data.data)
      }
    } catch (error) {
      setProducts([])
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // 分類／搜尋寫回網址，用 replace（同一畫面的參數微調，不佔瀏覽器歷史）
  useEffect(() => {
    const next = new URLSearchParams()
    if (activeTab !== 'all') next.set('tab', activeTab)
    if (searchText) next.set('q', searchText)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchText])

  // 進出編輯／新增頁時帶上來源網址，返回鍵在沒有站內歷史時才有正確的退路
  const listLocation = `${location.pathname}${location.search}`

  const handleAddProduct = () => {
    if (categories.length === 0) {
      message.warning('請先添加商品分類')
      setIsCategoryModalVisible(true)
      return
    }
    navigate('/inventory/products/add', { state: { from: listLocation } })
  }

  // 點擊商品卡的「進貨」按鈕：開啟 Modal，預選該商品
  const handleOpenPurchase = (productId) => {
    setPurchaseProductId(productId)
    setPurchaseModalNonce((n) => n + 1)
  }

  // Modal 關閉
  const handleClosePurchase = () => {
    setPurchaseProductId(null)
  }

  // Modal 內提交成功：關閉 Modal、刷新商品（庫存／統計會更新）
  const handlePurchaseSuccess = () => {
    setPurchaseProductId(null)
    fetchData()
  }

  const handleTabChange = (key) => {
    setActiveTab(key)
  }

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteInventoryProduct(productId)
      message.success('商品已刪除')
      fetchData()
    } catch (error) {
      console.error('刪除商品失敗：', error)
      const errorMessage = error.response?.data?.message || '刪除失敗'
      message.error(errorMessage)
    }
  }

  const handleCategorySuccess = () => {
    setIsCategoryModalVisible(false)
    setEditingCategory(null)
    fetchData()
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = filteredProducts.findIndex((p) => p._id === active.id)
    const newIndex = filteredProducts.findIndex((p) => p._id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const reorderedFiltered = arrayMove(filteredProducts, oldIndex, newIndex)
    const reorderedIds = reorderedFiltered.map((p) => p._id)
    const otherProducts = products.filter((p) => !reorderedIds.includes(p._id))
    const newProducts = [...reorderedFiltered, ...otherProducts]
    const productIds = newProducts.map((p) => p._id)
    setProducts(newProducts)
    setReordering(true)
    try {
      await reorderStoreProducts(productIds)
      message.success('商品排序已更新')
    } catch (err) {
      console.error('排序失敗', err)
      message.error(err.response?.data?.message || '排序失敗，請重試')
      fetchData()
    } finally {
      setReordering(false)
    }
  }

  const handleEditCategory = (category) => {
    setEditingCategory(category)
    setIsCategoryModalVisible(true)
  }

  // 處理刪除分類
  const handleDeleteCategory = async (categoryId) => {
    try {
      await deleteInventoryCategory(categoryId)
      message.success('分類已刪除')
      await fetchData()
      // 如果刪除的是當前選中的分類，切換到全部商品
      if (activeTab === categoryId) {
        setActiveTab('all')
      }
    } catch (error) {
      console.error('刪除分類失敗：', error)
      message.error(error.response?.data?.message || '刪除分類失敗')
    }
  }

  // 生成標籤頁數據
  const tabItems = [
    { key: 'all', label: '全部商品' },
    ...categories.map((cat) => ({
      key: cat._id,
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8
          }}
        >
          <span>{cat.name}</span>
          {canManageInventory && (
            <div
              style={{ display: 'flex', gap: 4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => handleEditCategory(cat)}
                style={{ padding: '0 4px' }}
              />
              <Popconfirm
                title='確定要刪除此分類嗎？'
                description='刪除分類後，該分類下的商品將不會被刪除，但會失去分類標記。'
                onConfirm={() => handleDeleteCategory(cat._id)}
                okText='確定'
                cancelText='取消'
              >
                <Button
                  type='text'
                  size='small'
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: '0 4px' }}
                />
              </Popconfirm>
            </div>
          )}
        </div>
      )
    }))
  ]

  const SortableProductCard = ({ product }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id: product._id })
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1
    }
    return (
      <div ref={setNodeRef} style={style}>
        {isSortMode && (
          <div
            style={{
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <DragHandle {...attributes} {...listeners}>
              <DragOutlined /> 拖曳排序
            </DragHandle>
          </div>
        )}
        <InventoryProductCard
          product={product}
          onDelete={handleDeleteProduct}
          onPurchase={handleOpenPurchase}
          onViewLedger={handleViewLedger}
          onAdjustStock={handleAdjustStock}
        />
      </div>
    )
  }

  // 過濾商品
  const filteredProducts = products.filter((product) => {
    // 支持多種分類 ID 格式
    const productCategoryId =
      product.categoryId || product.category?.id || product.category?._id
    const matchesTab =
      activeTab === 'all' ? true : productCategoryId === activeTab
    // 支持多種名稱格式
    const productName = product.name || product.title || ''
    const matchesSearch =
      !searchText ||
      productName.toLowerCase().includes(searchText.toLowerCase()) ||
      product.manufacturer?.toLowerCase().includes(searchText.toLowerCase())
    return matchesTab && matchesSearch
  })

  if (loading && !products.length) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <HeaderSection>
        <Title>
          <AppstoreOutlined style={{ marginRight: '8px' }} />
          庫存管理
        </Title>
        <ButtonGroup>
          {canManageInventory && (
            <Button
              icon={
                isSortMode ? <AppstoreOutlined /> : <SortAscendingOutlined />
              }
              onClick={() => setIsSortMode(!isSortMode)}
              loading={reordering}
            >
              {isSortMode ? '完成排序' : '排序'}
            </Button>
          )}
          {canManageInventory && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCategory(null)
                setIsCategoryModalVisible(true)
              }}
              disabled={
                categories.length >= getPlanLimit('productCategories')
              }
              title={
                categories.length >= getPlanLimit('productCategories')
                  ? `${planKey} 方案分類上限為 ${getPlanLimit(
                      'productCategories'
                    )} 個，請升級方案`
                  : ''
              }
            >
              新增分類（{categories.length}/{getPlanLimit('productCategories')}）
            </Button>
          )}
          {canManageInventory && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
              disabled={products.length >= getPlanLimit('products')}
              title={
                products.length >= getPlanLimit('products')
                  ? `${planKey} 方案商品上限為 ${getPlanLimit('products')} 個，請升級方案`
                  : ''
              }
            >
              新增/採購商品（{products.length}/{getPlanLimit('products')}）
            </Button>
          )}
        </ButtonGroup>
      </HeaderSection>

      {statistics && (
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={canViewFinance ? 6 : 8}>
            <StatsCard>
              <Statistic
                title='商品總數'
                value={statistics.totalProducts || 0}
                prefix={<AppstoreOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </StatsCard>
          </Col>
          <Col xs={12} sm={12} md={canViewFinance ? 6 : 8}>
            <StatsCard>
              <Statistic
                title='總庫存'
                value={statistics.totalStock || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </StatsCard>
          </Col>
          {canViewFinance && (
            <Col xs={12} sm={12} md={6}>
              <StatsCard>
                <Statistic
                  title='庫存價值'
                  value={statistics.totalStockValue || 0}
                  prefix='NT$'
                  valueStyle={{ color: '#d5728a' }}
                  precision={0}
                />
              </StatsCard>
            </Col>
          )}
          <Col xs={12} sm={12} md={canViewFinance ? 6 : 8}>
            <StatsCard>
              <Statistic
                title='低庫存商品'
                value={statistics.lowStockCount || 0}
                valueStyle={{ color: '#faad14' }}
              />
            </StatsCard>
          </Col>
        </Row>
      )}

      <SearchSection>
        <Search
          placeholder='搜索商品名稱或廠商'
          allowClear
          enterButton={<SearchOutlined />}
          size='large'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: '500px' }}
        />
      </SearchSection>

      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        type='card'
      />

      <ProductGrid>
        {filteredProducts.length > 0 ? (
          isSortMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredProducts.map((p) => p._id)}
                strategy={rectSortingStrategy}
              >
                {filteredProducts.map((product) => (
                  <SortableProductCard key={product._id || product.id} product={product} />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            filteredProducts.map((product) => (
              <InventoryProductCard
                key={product._id || product.id}
                product={product}
                onDelete={handleDeleteProduct}
                onPurchase={handleOpenPurchase}
                onViewLedger={handleViewLedger}
                onAdjustStock={handleAdjustStock}
              />
            ))
          )
        ) : (
          <EmptyState
            description={
              searchText
                ? '沒有找到符合條件的商品'
                : categories.length === 0
                ? canManageInventory
                  ? '請先添加商品分類'
                  : '尚未建立商品分類'
                : canManageInventory
                ? '暫無商品，點擊「添加商品」開始管理庫存'
                : '暫無商品'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {canManageInventory && categories.length === 0 && (
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={() => setIsCategoryModalVisible(true)}
              >
                添加分類
              </Button>
            )}
            {canManageInventory && categories.length > 0 && (
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={handleAddProduct}
              >
                添加商品
              </Button>
            )}
          </EmptyState>
        )}
      </ProductGrid>

      <Modal
        title={editingCategory ? '編輯分類' : '添加分類'}
        open={isCategoryModalVisible}
        onCancel={() => {
          setIsCategoryModalVisible(false)
          setEditingCategory(null)
        }}
        footer={null}
        width={500}
      >
        <InventoryCategoryForm
          category={editingCategory}
          onSuccess={handleCategorySuccess}
          onCancel={() => {
            setIsCategoryModalVisible(false)
            setEditingCategory(null)
          }}
        />
      </Modal>

      {/* 進貨 Modal：在商品管理頁直接快速進貨。
          手機版採全寬、貼齊頂部，並收緊 padding 以避免左右白邊浪費空間。 */}
      <Modal
        title={
          <span>
            <ShoppingCartOutlined style={{ marginRight: 8 }} />
            快速進貨
          </span>
        }
        open={purchaseProductId !== null}
        onCancel={handleClosePurchase}
        footer={null}
        width={isMobile ? '100%' : 1100}
        style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : undefined}
        destroyOnClose
        styles={{
          body: {
            paddingTop: isMobile ? 8 : 16,
            paddingLeft: isMobile ? 12 : undefined,
            paddingRight: isMobile ? 12 : undefined,
            paddingBottom: isMobile ? 12 : undefined,
            maxHeight: isMobile ? 'calc(100vh - 56px)' : undefined,
            overflowY: isMobile ? 'auto' : undefined
          },
          header: isMobile
            ? { paddingLeft: 12, paddingRight: 12 }
            : undefined,
          content: isMobile
            ? {
                padding: 0,
                borderRadius: 0,
                minHeight: '100vh'
              }
            : undefined
        }}
      >
        <InventoryPurchasePage
          key={`purchase-modal-${purchaseModalNonce}`}
          embedded
          preselectedProductId={purchaseProductId}
          onSuccess={handlePurchaseSuccess}
          onClose={handleClosePurchase}
        />
      </Modal>

      {/* 單品進出庫異動明細 Modal */}
      <ProductLedgerModal
        open={ledgerProduct !== null}
        productId={ledgerProduct?.id}
        productName={ledgerProduct?.name}
        onClose={() => setLedgerProduct(null)}
      />

      {/* 調整庫存 Modal（專用流程，會寫入異動明細） */}
      <StockAdjustModal
        open={adjustProduct !== null}
        product={adjustProduct}
        onClose={() => setAdjustProduct(null)}
        onSuccess={() => {
          setAdjustProduct(null)
          fetchData()
        }}
      />
    </PageContainer>
  )
}

export default InventoryProductsPage
